/**
 * Stage 1A — packaged-snack net grams (reviewed, optional).
 * Shown when the stock unit is mass (g/kg) or count (each). Net grams are
 * NOT a conversion: adding or correcting them never changes the stock unit,
 * conversions, posted deliveries, WAC or any snapshot.
 */
import React from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Scale, ShieldCheck } from 'lucide-react';
import { measureTypeOf } from './measureType';

export function stockUnitIndex(units) {
  const list = units || [];
  let i = list.findIndex(u => u.is_base_stock_unit);
  if (i === -1) i = list.findIndex(u => u.is_default_count_unit);
  return list.length ? Math.max(i, 0) : -1;
}

function ReviewStamp({ unit }) {
  const g = String(unit.net_grams ?? '').trim();
  const ev = String(unit.net_grams_evidence ?? '').trim();
  const changed = g !== String(unit._orig_net_grams ?? '') || ev !== String(unit._orig_net_grams_evidence ?? '');
  if (g && ev && (changed || !unit.net_grams_reviewed_at)) {
    return <span className="text-sky-700" data-testid="net-grams-stamp">Will be recorded as reviewed by you when saved</span>;
  }
  if (g && ev && unit.net_grams_reviewed_at) {
    const when = new Date(unit.net_grams_reviewed_at).toLocaleDateString('en-GB');
    return (
      <span className="inline-flex items-center gap-1 text-emerald-700" data-testid="net-grams-stamp">
        <ShieldCheck className="h-3 w-3" /> Reviewed by {unit.net_grams_reviewed_by_name || 'a reviewer'} on {when}
      </span>
    );
  }
  return <span className="text-slate-500" data-testid="net-grams-stamp">Not reviewed — not used in any calculation</span>;
}

export function SnackNetGramsPanel({ form, setForm }) {
  const idx = stockUnitIndex(form.units);
  const unit = idx >= 0 ? form.units[idx] : null;
  if (!unit) return null;
  const mt = measureTypeOf(unit.base_unit);
  if (mt !== 'mass' && mt !== 'count') return null;

  const update = (patch) => setForm(f => ({
    ...f, units: f.units.map((u, i) => (i === idx ? { ...u, ...patch } : u)),
  }));
  const unitGrams = mt === 'mass' && unit.conversion_to_base
    ? parseFloat(unit.conversion_to_base) * ((unit.base_unit || '').toLowerCase() === 'kg' ? 1000 : 1)
    : null;
  const gramsMissingEvidence = String(unit.net_grams ?? '').trim() && !String(unit.net_grams_evidence ?? '').trim();

  return (
    <div className="rounded-lg border border-sky-200 bg-sky-50/60 px-4 py-3 space-y-2.5" data-testid="snack-net-grams-panel">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-sky-900 uppercase tracking-wide">
        <Scale className="h-3.5 w-3.5" /> Packaged snack — net weight ({unit.unit_type || 'stock unit'})
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="space-y-1">
          <label className="text-xs font-medium text-slate-700" htmlFor="snack-net-grams">Net grams per {unit.unit_type || 'unit'}</label>
          <Input id="snack-net-grams" type="number" min="0" step="0.1" className="h-8 text-xs"
            value={unit.net_grams ?? ''} placeholder="optional"
            onChange={(e) => update({ net_grams: e.target.value })}
            data-testid="snack-net-grams" />
        </div>
        <div className="space-y-1 sm:col-span-2">
          <label className="text-xs font-medium text-slate-700" htmlFor="snack-net-grams-evidence">Evidence</label>
          <Input id="snack-net-grams-evidence" className="h-8 text-xs"
            value={unit.net_grams_evidence ?? ''} placeholder="e.g. pack label, supplier spec sheet"
            onChange={(e) => update({ net_grams_evidence: e.target.value })}
            data-testid="snack-net-grams-evidence" />
        </div>
      </div>
      {unitGrams && !String(unit.net_grams ?? '').trim() && (
        <Button type="button" variant="outline" size="sm" className="h-7 text-xs"
          onClick={() => update({ net_grams: String(unitGrams) })} data-testid="snack-net-grams-use-unit-size">
          Use {unitGrams} g from the unit size (you still need to add evidence)
        </Button>
      )}
      {gramsMissingEvidence && (
        <p className="text-[11px] text-amber-700" data-testid="snack-net-grams-evidence-required">Add evidence to record reviewed net grams.</p>
      )}
      <div className="text-[11px] flex flex-wrap items-center justify-between gap-2">
        <ReviewStamp unit={unit} />
        <span className="text-slate-500">Never changes stock, conversions, prices or history.</span>
      </div>
    </div>
  );
}
